<template>
    <form action="/banking/saveamounttocart" data-quickreg="False" method="POST">
      <input :value="sku" name="productSku" type="hidden">
      <div class="row fields" style="margin-left: 0; margin-right: 0;">
        <div class="col s12 m12 l9 step-one-col">
          <label data-t="Amount sent">
            {{ t("Amount sent") }}
            <span class="span-advice-country-limit-exceeded" style="display: contents;">
              (max. {{ transferSummaryModel.MaxSentAmountCountryLimitInSenderCurrency }} {{ transferSummaryModel.SentCurrency }} )
            </span>
          </label>
          <div class="moneyInputField senderAmount" style="height:55px">
            <div class="moneyLeftField">
              <input v-model="senderAmount" autocomplete="off" class="no-spin-number-input" name="senderAmount"
                     placeholder="0"
                     step="any" style="color: rgb(0, 0, 0);" type="number"
                     @blur="focusSenderAmount(false)" @focus="focusSenderAmount(true)">
            </div>

            <sender-multicurrency :model="senderMulticurrencyModel"
                                  :sku="sku"
                                  :transferSummaryModel="transferSummaryModel"
                                  :updateCurrencyUrl="updateCurrencyUrl"
                                  v-on:senderCurrencyUpdated="handleSenderCurrencyUpdated"/>
          </div>
        </div>
        <div v-if="displayMaxAmountWarning" class="validation-advice" data-t="Please choose a smaller amount.">
          {{ t("Please choose a smaller amount.") }}
          <span class="span-advice-country-limit-exceeded">
              {{ transferSummaryModel.MaxSentAmountCountryLimitInSenderCurrency }} {{ transferSummaryModel.SentCurrency }}
          </span>
        </div>
        <div v-if="displayMinAmountWarning" class="validation-advice" data-t="The minimum amount for a transfer is">
          {{ t("The minimum amount for a transfer is") }}
          <span class="span-advice-too-little-money" style="display: contents;">
              {{ transferSummaryModel.MinimumAllowedTransferAmountInSenderCurrency }} {{ transferSummaryModel.SentCurrency }}
          </span>
        </div>
      </div>
      <div class="row fields" style="margin-left:0; margin-right:0;  margin-top:10px">
        <div class="col s12 m12 l9 step-one-col">
          <div>
            <label data-t="Receivable amount"> {{ t("Receivable amount") }} </label>
            <div class="moneyInputField receiverAmount">
              <div style="height:55px">
                <div class="moneyLeftField">
                  <input v-model="receiverAmount" autocomplete="off" class="no-spin-number-input" name="receiverAmount"
                         placeholder="0"
                         step="any" style="color: rgb(0, 0, 0);" type="number"
                         @blur="focusReceiverAmount(false)" @focus="focusReceiverAmount(true)">
                </div>

                <receiver-multicurrency :model="receiverMulticurrencyModel"
                                        :transferSummaryModel="transferSummaryModel"
                                        :updateCurrencyUrl="updateCurrencyUrl"
                                        v-on:receiverCurrencyUpdated="handleReceiverCurrencyUpdated"/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row fields continue-button-on-banking-amount">
        <div class="col s12 m12 l9 step-one-col checking">
          <input :disabled='isSubmitButtonActive === false' :value="t('Continue')"
                 class="btn-large btn-large-banking check load-more"
                 data-t="Continue" type="submit" v-on:click.stop="submitDataForm">
        </div>
      </div>
    </form>
</template>


<script lang="ts">
import {defineComponent} from 'vue'
import ReceiverMultiCurrency from './receiverMulticurrency.vue';
import SenderMultiCurrency from './senderMulticurrency.vue';
import {
  AmountsWithTransferSummaryModel,
  MulticurrencyModel,  
  MulticurrencyItemModel,
  TransferSummaryModel
} from '../Models/AmountsWithTransferSummaryModel';

export default defineComponent({
  name: "amounts",
  data: function () {
    return {
      receiverAmount: 0,
      senderAmount: 0,
      displayMaxAmountWarning: false,
      displayMinAmountWarning: false,
      isSubmitButtonActive: true,
      senderAmountFocused: false,
      receiverAmountFocused: false,
    }
  },
  watch: {
    transferSummaryModel: function (newVal: TransferSummaryModel, _: TransferSummaryModel) {
      this.senderAmount = newVal.AmountSent;
      this.validateSubmitButton();
    },
    receiverAmount: function (newVal: number, _: number) {
      if (this.senderAmountFocused)
        return;

      const receiverAmount = this._roundTo(Number(newVal));
      const transferFee = this._roundTo(this.transferSummaryModel.TransferFee);
      const paymentFee = this._roundTo(this.transferSummaryModel.PaymentFee);
      const exchangeRate = this._roundTo(this.transferSummaryModel.ExchangeRate, 4);

      const senderAmount = this._roundTo(receiverAmount / exchangeRate);
      const total = this._roundTo(senderAmount + transferFee + paymentFee);
      this.senderAmount = senderAmount;

      this.$emit('amountUpdated', senderAmount, total, receiverAmount);
    },
    senderAmount: function (newVal: number, _: number) {
      if (this.receiverAmountFocused)
        return;

      const senderAmount = this._roundTo(Number(newVal));
      const isValid = this.validateSenderAmount(senderAmount);
      this.isSubmitButtonActive = isValid;
      if (!isValid)
        return;

      const transferFee = this._roundTo(this.transferSummaryModel.TransferFee);
      const paymentFee = this._roundTo(this.transferSummaryModel.PaymentFee);
      const exchangeRate = this._roundTo(this.transferSummaryModel.ExchangeRate, 4);

      const receiverAmount = this._roundTo(senderAmount * exchangeRate);
      const total = this._roundTo(senderAmount + transferFee + paymentFee);
      this.receiverAmount = receiverAmount;

      this.$emit('amountUpdated', senderAmount.toFixed(2), total.toFixed(2), this.receiverAmount);
    },
    receiverMulticurrencyModel: function(newVal: MulticurrencyModel, _: MulticurrencyModel ) {

        var isProductActivated = this.isReceiverProductActivated(newVal.DefaultSelectedValue.ProductSku);

        this.isSubmitButtonActive = isProductActivated;
    },
},
  props: {
    transferSummaryModel: Object as () => TransferSummaryModel,
    senderMulticurrencyModel: Object as () => MulticurrencyModel,
    receiverMulticurrencyModel: Object as () => MulticurrencyModel,
    sku: String,
    isQuickRegistrationRequired: Boolean,
    updateCurrencyUrl: String,
  },
  components: {
    "ReceiverMulticurrency": ReceiverMultiCurrency,
    "SenderMulticurrency": SenderMultiCurrency,
  },
  methods: {
    _roundTo(num: number, digits: number = 2): number {
      const fixed = num.toFixed(digits);
      return parseFloat(fixed);
    },
    focusReceiverAmount(focused: Boolean) {
      this.receiverAmountFocused = focused;
    },
    focusSenderAmount(focused: Boolean) {
      this.senderAmountFocused = focused;
    },
    validateSenderAmount(senderAmount: number): Boolean {
      if (senderAmount < this.transferSummaryModel.MinimumAllowedTransferAmountInSenderCurrency) {
        this.displayMinAmountWarning = true;
        return false;
      }
      if (senderAmount > this.transferSummaryModel.MaxSentAmountCountryLimitInSenderCurrency) {
        this.displayMaxAmountWarning = true;
        return false;
      }
      this.displayMinAmountWarning = false;
      this.displayMaxAmountWarning = false;
      return true;
    },
    validateSubmitButton() {

       var isSenderAmountValidated = this.validateSenderAmount(this.senderAmount);
       var isReceiverProductActivated = this.isReceiverProductActivated(this.transferSummaryModel.Product.Sku);

       this.isSubmitButtonActive = isSenderAmountValidated && isReceiverProductActivated;
    },
    submitDataForm(event: SubmitEvent): Boolean {

      if (this.isQuickRegistrationRequired) {
        (window as any).bindQuickRegModal(event);
        event.preventDefault();
        return false;
      }

      return true;
    },
    isReceiverProductActivated(productSku: string): Boolean {
        var isActivated = this.receiverMulticurrencyModel.DeactivatedItems.
                findIndex((obj: MulticurrencyItemModel) => 
                    obj.ProductSku === productSku) == -1;

        return isActivated;
    },
    handleReceiverCurrencyUpdated(response: AmountsWithTransferSummaryModel) {
      this.$emit('receiverCurrencyUpdated', response);
    },
    handleSenderCurrencyUpdated(response: AmountsWithTransferSummaryModel) {
      this.$emit('senderCurrencyUpdated', response);
    }
  }
});
</script>
